import React, { useCallback, useState } from 'react';
import {
  AppButton,
  Box,
  CrossIcon,
  EmptyServicesListIcon,
  Inscription,
  Text,
  WarningIcon,
  WarnModal,
} from '@common-fe/common-fe';

import { PayoutOrderCategory, PayoutOrderPlan } from '../../../PayoutDefinitionSetup.types';
import { usePayoutOrderColumns, usePayoutOrderPlans } from '../hooks';
import useRemoveColumnFlow from '../hooks/useRemoveColumnFlow';
import { AnimatedCategoryName, AnimatedColumn, RemoveServiceButton } from '../PayoutOrder.styles';

import { CATEGORY_NAME_WIDTH_MAP, COLUMN_WIDTH_MAP } from './PayoutOrder.constants';
import PayoutOrderCategoryCard from './PayoutOrderCategoryCard';
import PayoutOrderServicesList from './PayoutOrderServicesList';

interface PayoutOrderProps {
  categories: PayoutOrderCategory[];
  onRemoveCategory: (categoryType: string) => void;
  onRemoveService: (categoryType: string, rowId: string) => void;
  isEditMode: boolean;
  showValidationErrors?: boolean;
}

const PayoutOrder: React.FC<PayoutOrderProps> = ({
  categories,
  onRemoveCategory,
  onRemoveService,
  isEditMode,
  showValidationErrors,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const { columns, columnsCount, canRemoveColumn } = usePayoutOrderColumns();
  const { handleUpdateServicePlan, handleUpdateCategoryPlan } = usePayoutOrderPlans();
  const {
    isRemoveColumnModalVisible,
    handleRequestRemoveColumn,
    handleConfirmRemoveColumn,
    handleCancelRemoveColumn,
  } = useRemoveColumnFlow(categories);

  const handleToggleCategory = useCallback((categoryType: string) => {
    setExpandedCategories((prev) => {
      const isCurrentlyExpanded = prev[categoryType];
      if (isCurrentlyExpanded) {
        return { ...prev, [categoryType]: false };
      }
      const next: Record<string, boolean> = {};
      next[categoryType] = true;
      return next;
    });
  }, []);

  if (categories.length === 0) {
    return (
      <Box data-testid="payout-order_wrapper">
        <Box
          background="module"
          round="container1Round"
          align="center"
          justify="center"
          gap="spacing12"
          pad="spacing24"
        >
          <EmptyServicesListIcon color="iconSecondary" />
          <Text color="textBody" weight="bold" size="large">
            There are no Services yet
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box data-testid="payout-order_wrapper" background="module">
      <Box>
        <Box
          direction="row"
          align="center"
          pad={{ vertical: 'spacing8', horizontal: 'spacing24' }}
          margin={{ bottom: 'spacing12' }}
          background="legend"
          round="container2Round"
          style={{ height: '36px', paddingRight: '72px' }}
          gap="s"
        >
          <AnimatedCategoryName style={{ width: CATEGORY_NAME_WIDTH_MAP[columnsCount] }}>
            <Inscription size="12px" weight="bold" lineHeight="22px">
              Category
            </Inscription>
          </AnimatedCategoryName>
          {columns.map((columnIndex) => (
            <AnimatedColumn key={columnIndex} style={{ width: COLUMN_WIDTH_MAP[columnsCount] }}>
              <Box direction="row" align="center" gap="spacing8">
                <Inscription size="12px" weight="bold" lineHeight="22px">
                  Pays {columnIndex}
                </Inscription>
                {isEditMode && columnIndex > 1 && canRemoveColumn && (
                  <RemoveServiceButton
                    data-testid={`payout-order_column_${columnIndex}_remove`}
                    onClick={() => handleRequestRemoveColumn(columnIndex)}
                  >
                    <CrossIcon size="10px" color="iconPrimary" />
                  </RemoveServiceButton>
                )}
              </Box>
            </AnimatedColumn>
          ))}
        </Box>

        <Box gap="spacing8">
          {categories.map((category) => (
            <MemoizedCategoryWithServices
              key={category.categoryType}
              category={category}
              isExpanded={!!expandedCategories[category.categoryType]}
              onToggleExpand={handleToggleCategory}
              onRemoveCategory={onRemoveCategory}
              onRemoveService={onRemoveService}
              isEditMode={isEditMode}
              columns={columns}
              columnsCount={columnsCount}
              onUpdateCategoryPlan={handleUpdateCategoryPlan}
              onUpdateServicePlan={handleUpdateServicePlan}
              showValidationErrors={showValidationErrors}
            />
          ))}
        </Box>
      </Box>

      <WarnModal
        testId="payout-order_remove-column-modal"
        iconNode={<WarningIcon size="48px" />}
        visible={isRemoveColumnModalVisible}
        header="Remove Column?"
        helptext="Removing this column will reset all selected plans and update the payment order for all categories."
        onSetVisible={(visible) => {
          if (!visible) handleCancelRemoveColumn();
        }}
        cancelButtonNode={
          <Box direction="row" gap="spacing12">
            <AppButton
              testId="payout-order_remove-column-cancel"
              buttonType="secondary"
              onClick={() => handleCancelRemoveColumn()}
              width="150px"
            >
              Cancel
            </AppButton>
            <AppButton
              testId="payout-order_remove-column-confirm"
              color="red"
              onClick={handleConfirmRemoveColumn}
              width="150px"
            >
              Yes, Remove
            </AppButton>
          </Box>
        }
      />
    </Box>
  );
};

interface CategoryWithServicesProps {
  category: PayoutOrderCategory;
  isExpanded: boolean;
  isEditMode: boolean;
  columns: number[];
  columnsCount: number;
  showValidationErrors?: boolean;
  onToggleExpand: (categoryType: string) => void;
  onRemoveCategory: (categoryType: string) => void;
  onRemoveService: (categoryType: string, rowId: string) => void;
  onUpdateCategoryPlan: (categoryType: string, order: number, plan: PayoutOrderPlan | null) => void;
  onUpdateServicePlan: (params: {
    categoryType: string;
    rowId: string;
    order: number;
    plan: PayoutOrderPlan | null;
  }) => void;
}

const CategoryWithServices: React.FC<CategoryWithServicesProps> = ({
  category,
  isExpanded,
  isEditMode,
  columns,
  columnsCount,
  showValidationErrors,
  onToggleExpand,
  onRemoveCategory,
  onRemoveService,
  onUpdateCategoryPlan,
  onUpdateServicePlan,
}) => {
  const handleToggle = useCallback(
    () => onToggleExpand(category.categoryType),
    [onToggleExpand, category.categoryType]
  );

  const handleRemove = useCallback(
    () => onRemoveCategory(category.categoryType),
    [onRemoveCategory, category.categoryType]
  );

  const handleRemoveService = useCallback(
    (rowId: string) => onRemoveService(category.categoryType, rowId),
    [onRemoveService, category.categoryType]
  );

  const handleUpdateCategoryPlan = useCallback(
    (order: number, plan: PayoutOrderPlan | null) => onUpdateCategoryPlan(category.categoryType, order, plan),
    [onUpdateCategoryPlan, category.categoryType]
  );

  return (
    <PayoutOrderCategoryCard
      categoryType={category.categoryType}
      serviceCount={category.services.length}
      services={category.services}
      isExpanded={isExpanded}
      onToggleExpand={handleToggle}
      onRemove={handleRemove}
      isEditMode={isEditMode}
      columns={columns}
      onUpdateCategoryPlan={handleUpdateCategoryPlan}
      columnsCount={columnsCount}
      showValidationErrors={showValidationErrors}
    >
      {isExpanded && (
        <PayoutOrderServicesList
          services={category.services}
          isEditMode={isEditMode}
          onRemoveService={handleRemoveService}
          columns={columns}
          categoryType={category.categoryType}
          onUpdateServicePlan={onUpdateServicePlan}
          columnsCount={columnsCount}
        />
      )}
    </PayoutOrderCategoryCard>
  );
};

const MemoizedCategoryWithServices = CategoryWithServices;

export default PayoutOrder;
